import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  SortingState,
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
} from '@tanstack/react-table';
import { Dialog, Button, Pagination, MenuItem, Select } from '@dsi/react-eds';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import api from '../../../api/axiosInstance';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useLoader } from '../../../hooks/useLoader';
import { useTheme } from '../../../hooks/useTheme';
import { AxiosError } from 'axios';
import styles from './ConfigurationInstanceList.module.css';
import { automationFlowService } from '../../../services/automationFlowService';
import { AutomationFlow } from '../../../types/automation-flow.types';
import { cnisService } from '../../../services/cnisService';

export interface EnrichedAutomationFlow extends AutomationFlow {
  productLineName?:string;
  eiapClusterName?:string;
  enmNames?:string[];
}

function ConfigurationInstanceList() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();
  const [automationFlows, setAutomationFlows] = useState<EnrichedAutomationFlow[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<EnrichedAutomationFlow | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [debouncedFilters, setDebouncedFilters] = useState<ColumnFiltersState>([]);
  const [filterTimeout, setFilterTimeout] = useState<NodeJS.Timeout | null>(null);
  const [total, setTotal] = useState<number>(0);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10
  });
  const fetchAutomationFlows = async () => {
    try {
      showLoader();

      const sortField = sorting[0]?.id || 'created_at';
      const sortDirection = sorting[0]?.desc ? 'DESC' : 'ASC';
      const filters = debouncedFilters.reduce((acc, filter) => {
        if (filter.id === 'status' && Array.isArray(filter.value)) {
          acc[filter.id] = filter.value.join(',');
        } else {
          acc[filter.id] = filter.value;
        }
        return acc;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, {} as any);
      console.log('API Request params:', {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        filters,
        sortBy: sortField,
        sortOrder: sortDirection
      });

      const flowsResp = await automationFlowService.getAll(pagination.pageIndex + 1,
        pagination.pageSize,
        filters,
        sortField,
        sortDirection);
      const flows: EnrichedAutomationFlow[] = flowsResp.data || [];
      const clustersResp = await api.get('/api/eiap-cluster');
      const clusters = clustersResp.data || [];
      const clusterMap = new Map(clusters.map((c: any) => [c.id, c.name]));
      const cnisRes = await cnisService.getAll();
      const cnisEndpoints = cnisRes.data || [];
      const cnisMap = new Map(cnisEndpoints.map((ce: any) => [ce.id, ce.name]))

      setAutomationFlows(
        flows.map((flow): EnrichedAutomationFlow => ({
          ...flow,
          productLineName: flow.productType?.productTypeProductLines?.map(pl => pl.productLineName).join(', ') || flow.productType?.name || 'Unknown',
          eiapClusterName: (clusterMap.get(flow.eiapClusterId) as string) || (cnisMap.get(flow.cnisEndpointId) as string) || 'Unknown',
          enmNames: flow.enms?.map((enm: any) => enm.name || `ENM-${enm.id.substring(0, 8)}`) || [],
        }))
      );
      setTotal(flowsResp.total);
    } catch (error: unknown) {
      // setAutomationFlows([]);
      addNotifications([{
        title: 'Error - Automation Flows',
        icon: 'icon-triangle-warning',
        description: error instanceof AxiosError
          ? error.response?.data?.message || error.message
          : 'An error occurred while fetching automation flows.',
        color: 'red',
        timestamp: new Date(),
      }]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  useEffect(() => {
    fetchAutomationFlows();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedFilters]);

  const handleEditFlow = (flowId: string, productTypeName?: string) => {
    navigate('/automation-flows/new/wizard', { state: { flowId, mode: 'edit', preSelectedProductType: productTypeName } });
  };

  const openDeleteDialog = (flow: AutomationFlow) => {
    setFlowToDelete(flow);
    setDeleteDialogOpen(true);
  };

  const handleDeleteFlow = async () => {
    if (!flowToDelete) return;
    try {
      showLoader();
      await api.delete(`/api/automation-flow/${flowToDelete.id}`);
      addNotifications([{
        title: 'Success',
        icon: 'icon-success',
        description: 'Automation flow deleted successfully!',
        timestamp: new Date(),
        color: 'green',
      }]);
      await fetchAutomationFlows();
    } catch (error) {
      addNotifications([{
        title: 'Error',
        icon: 'icon-triangle-warning',
        description: error instanceof AxiosError
          ? error.response?.data?.message || error.message
          : 'An error occurred while deleting the automation flow',
        timestamp: new Date(),
        color: 'red',
      }]);
    } finally {
      hideLoader();
      setDeleteDialogOpen(false);
      setFlowToDelete(null);
    }
  };

  const columns = useMemo<ColumnDef<EnrichedAutomationFlow>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ getValue }) => (
        <span title={getValue() as string}>{getValue() as string}</span>
      )
    },
    { accessorKey: 'description', header: 'Description', title: 'Description' },
    {
      id: 'cardName',
      header: 'Template',
      accessorFn: (row) => row.cardName,
    },
    {
      id: 'productLine',
      header: 'Product Lines',
      accessorFn: (row) => row.productLineName,
    },
    {
      id: 'environment',
      header: 'Environment',
      accessorFn: (row) => row.eiapClusterName,
    },
    {
      id: 'enms',
      header: 'ENMs',
      accessorFn: (row) => row.enmNames && row.enmNames.length > 0 ? row.enmNames.join(', ') : 'None',
    },
    /*
    cell: ({ row }) => (
        <>
          <div>
            <strong>{row.original.eiapClusterName || 'Unknown'}</strong>
          </div>
          <div style={{ fontSize: '11px', color: '#6a6a6a', marginTop: '4px' }}>
            ENMs: {row.original.enmNames && row.original.enmNames.length > 0 ? row.original.enmNames.join(', ') : 'None'}
          </div>
        </>
      ),*/
    {
      accessorKey: 'lastRun',
      header: 'Last Run',
      cell: ({ getValue }) => getValue() ? new Date(getValue() as string).toISOString().split('T')[0] : 'Never Run',
      enableSorting: false,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue() as string;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <i
              className="icon icon-alarm-level4"
              style={{
                fontSize: '8px',
                color: status === 'active' ? '#28a745' : status === 'error' ? '#dc3545' : '#6a6a6a',
                marginRight: '6px',
              }}
            />
            <span style={{ textTransform: 'capitalize' }}>{status}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Creation Date',
      cell: ({ getValue }) => new Date(getValue() as string).toISOString().split('T')[0],
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className={styles.actionButtons}>
          <ReactEDSWrapper component={Button} icon="edit" onClick={() => handleEditFlow(row.original.id, row.original.productType?.name)} />
          <ReactEDSWrapper component={Button} icon="trashcan" onClick={() => openDeleteDialog(row.original)} />
        </div>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);
  const table = useReactTable({
    data: automationFlows,
    columns: columns,
    pageCount: Math.ceil(total / pagination.pageSize),
    state: {
      pagination,
      columnFilters,
      sorting
    },
    onPaginationChange: setPagination,
    onColumnFiltersChange: (updater) => {
      const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
      setColumnFilters(newFilters);
      if (filterTimeout) clearTimeout(filterTimeout);
      const timeout = setTimeout(() => {
        setDebouncedFilters(newFilters);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }, 500);
      setFilterTimeout(timeout);
    },
    onSortingChange: (updater) => {
      setSorting(updater);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    enableMultiSort: false,
    enableSortingRemoval: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true
  });

  const hasActiveFilters = debouncedFilters.length > 0;

  return (
    <div className={styles.container}>
      {automationFlows.length === 0 && !hasActiveFilters ? (
        <div className={styles.noDataContainer}>
          <i className={`icon icon-info ${styles.dangerIcon}`} />
          <div className={styles.noDataTitle}>No Configuration Instances Found</div>
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <span className={styles.itemCount}>
              {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}-
              {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, total)} of {total} items
            </span>
            <div className={styles.buttonGroup}>
              <ReactEDSWrapper component={Button} icon={showFilters ? 'filter-off' : 'filter'} onClick={() => setShowFilters(!showFilters)}>
                {showFilters ? 'Close Filters' : ' Filters'}
              </ReactEDSWrapper>
              {showFilters && (
                <ReactEDSWrapper
                  component={Button}
                  icon="filter-clear"
                  onClick={() => {
                    setColumnFilters([]);
                    setDebouncedFilters([]);
                    setPagination({ pageIndex: 0, pageSize: 10 });
                  }}
                >
                  Clear Filters
                </ReactEDSWrapper>
              )}
            </div>
          </div>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className={styles.headerRow}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} className={styles.headerCell}>
                        <div
                          className={header.column.getCanSort() ? styles.sortableHeader : undefined}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className={styles.sortIcon}>
                              {header.column.getIsSorted() === 'asc' ? (
                                <i className="icon icon-sort-up" style={{ transform: 'rotate(180deg)' }} />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <i className="icon icon-sort-down" />
                              ) : (
                                <i className="icon icon-sorting" />
                              )}
                            </span>
                          )}
                        </div>
                        {header.column.getCanFilter() && showFilters ? (
                          <div className={styles.filterContainer}>
                            {header.column.id === 'status' ? (
                              <ReactEDSWrapper
                                component={Select}
                                type="multi"
                                label="Select options"
                                style={{ fontWeight: 'normal' }}
                                value={header.column.getFilterValue() || []}
                                onChange={(event: React.FormEvent<HTMLElement>) => {
                                  header.column.setFilterValue(event);
                                }}
                              >
                                <ReactEDSWrapper component={MenuItem} value='active'>
                                  Active
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value='inactive'>
                                  Inactive
                                </ReactEDSWrapper>
                                <ReactEDSWrapper component={MenuItem} value='error'>
                                  Error
                                </ReactEDSWrapper>
                              </ReactEDSWrapper>
                            ) : (
                              <input
                                type="text"
                                value={(header.column.getFilterValue() ?? '') as string}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                placeholder={`Filter`}
                                className={styles.filterInput}
                              />
                            )}
                          </div>
                        ) : null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={table.getAllColumns().length} style={{ textAlign: 'center', padding: '40px' }}>
                      <i className={`icon icon-info ${styles.dangerIcon}`} />
                      <div className={styles.noDataTitle}>No results match your filters</div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={styles.tableRow}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={styles.tableCell}
                        style={{ borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
            <ReactEDSWrapper
              component={Pagination}
              numPages={table.getPageCount()}
              selectedPage={table.getState().pagination.pageIndex + 1}
              onPaginationChangePage={(page: number) => {
                table.setPageIndex(page - 1);
              }}
              optionsList={[
                { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
                { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
                { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
                { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
                { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
              ]}
              onPaginationChangeSelect={(size: string) => {
                table.setPageSize(Number(size));
                table.setPageIndex(0);
              }}
              withActions
            />
        </>
      )}

      {deleteDialogOpen && (
        <ReactEDSWrapper
          component={Dialog}
          title="Delete Configuration"
          subtitle="Workflow Configuration Instance"
          show={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          bottom={
            <ReactEDSWrapper component={Button} warning onClick={handleDeleteFlow}>
              Delete Flow
            </ReactEDSWrapper>
          }
        >
          <p>
            You are about to remove automation flow '{flowToDelete?.name}' from the list.<br />
            This action is permanent and cannot be undone.<br />
            Are you sure you want to continue?
          </p>
        </ReactEDSWrapper>
      )}
    </div>
  );
}

export default ConfigurationInstanceList;
